import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Award,
  Star,
  Send,
  Trash2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Lock,
  RefreshCw,
} from "lucide-react";
import {
  useAdminGetCourseGradesQuery,
  useAdminSubmitCourseGradeMutation,
  useAdminDeleteCourseGradeMutation,
  useAdminCompileResultMutation,
  useAdminReleaseResultMutation,
  useAdminOverrideResultMutation,
  useAdminGetAppResultMetaQuery,
} from "../../api/examsApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

function safeMsg(err: any) {
  return err?.data?.message || err?.error || err?.message || "Something went wrong";
}

const STATUS_OPTIONS = [
  { value: "passed", label: "Passed" },
  { value: "failed", label: "Failed" },
  { value: "partial_pass", label: "Partial Pass" },
  { value: "recommended", label: "Recommended" },
  { value: "not_recommended", label: "Not Recommended" },
];

export default function AdminCourseGradingPage() {
  const { id: appId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch, isFetching } =
    useAdminGetCourseGradesQuery(appId!, { skip: !appId });

  const { data: resultMeta } = useAdminGetAppResultMetaQuery(appId!, { skip: !appId });

  const [submitGrade, { isLoading: submitting }] = useAdminSubmitCourseGradeMutation();
  const [deleteGrade, { isLoading: deleting }] = useAdminDeleteCourseGradeMutation();
  const [compile, { isLoading: compiling }] = useAdminCompileResultMutation();
  const [release, { isLoading: releasing }] = useAdminReleaseResultMutation();
  const [override, { isLoading: overriding }] = useAdminOverrideResultMutation();

  // The emergency override is the only mutation this page actually exposes —
  // handleSubmitGrade/handleCompile/handleRelease above are defined but wired
  // to no control, so there is nothing else here to gate.
  const { can } = useAccess();
  const canOverride = can.perm(PERMS.exam.resultPublish);

  const [forms, setForms] = useState<Record<number, { score: string; remarks: string }>>({});
  const [compileStatus, setCompileStatus] = useState("passed");
  const [compileRemarks, setCompileRemarks] = useState("");

  // Emergency override (bypasses the ≥3-examiner average). The chief scores
  // every registered course; the overall result is derived from those scores.
  const [showOverride, setShowOverride] = useState(false);
  const [ovReason, setOvReason] = useState("");
  const [ovPassMark, setOvPassMark] = useState("50");
  const [ovPublish, setOvPublish] = useState(true);
  // Keyed by the unit's course_id as a string — an interview's single unit is
  // addressed by the literal "panel", not a numeric course id.
  const [ovScores, setOvScores] = useState<Record<string, string>>({});
  const [flashError, setFlashError] = useState<string | null>(null);
  const [flashSuccess, setFlashSuccess] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  // The endpoint returns { data: [...courses] }; the RTK `pick` transform already
  // unwraps one level, so `data` is the array itself. Guard for both shapes.
  const courses: any[] = Array.isArray(data) ? data : (data?.data ?? []);

  // Interview exams (PCI / MRI) have no courses — the backend returns a single
  // unit, the interview itself, and its outcome is recommended / not recommended
  // rather than passed / failed.
  const isInterview = courses.some((c) => c?.is_interview);
  const unitLabel = isInterview ? "Interview" : "Registered Courses";

  function getForm(courseId: number) {
    return forms[courseId] ?? { score: "", remarks: "" };
  }

  function setForm(courseId: number, patch: Partial<{ score: string; remarks: string }>) {
    setForms((prev) => ({
      ...prev,
      [courseId]: { ...getForm(courseId), ...patch },
    }));
  }

  async function handleSubmitGrade(courseId: number) {
    if (!appId) return;
    const form = getForm(courseId);
    const score = parseFloat(form.score);
    if (isNaN(score) || score < 0 || score > 100) {
      setFlashError("Score must be between 0 and 100.");
      return;
    }
    setFlashError(null);
    try {
      await submitGrade({
        appId,
        body: { exam_course_id: courseId, score, remarks: form.remarks || undefined },
      }).unwrap();
      setForms((prev) => ({ ...prev, [courseId]: { score: "", remarks: "" } }));
      setFlashSuccess("Grade submitted.");
      setTimeout(() => setFlashSuccess(null), 3000);
    } catch (e) {
      setFlashError(safeMsg(e));
    }
  }

  async function confirmDeleteGrade() {
    if (!pendingDelete) return;
    setFlashError(null);
    try {
      await deleteGrade({ gradeId: pendingDelete, appId: appId! }).unwrap();
    } catch (e) {
      setFlashError(safeMsg(e));
    } finally {
      setPendingDelete(null);
    }
  }

  async function handleCompile() {
    if (!appId) return;
    setFlashError(null);
    try {
      const res = await compile({
        appId,
        body: { overall_status: compileStatus, remarks: compileRemarks || undefined },
      }).unwrap();
      setFlashSuccess(
        `Result compiled (draft). Overall average: ${res.overall_average ?? "—"}. Review, then Release.`
      );
      setTimeout(() => setFlashSuccess(null), 6000);
    } catch (e: any) {
      // Surface incomplete-course details from the backend (needs 3 examiners / marks)
      const incomplete = e?.data?.incomplete_courses;
      if (Array.isArray(incomplete) && incomplete.length) {
        const names = incomplete.map((c: any) => c.course_code || c.course_name).join(", ");
        setFlashError(`Cannot compile — these courses need 3 assigned examiners and all marks in: ${names}`);
      } else {
        setFlashError(safeMsg(e));
      }
    }
  }

  async function handleRelease() {
    if (!appId) return;
    setFlashError(null);
    try {
      await release({ appId }).unwrap();
      setFlashSuccess("Result published. The candidate sees it once the secretariat releases the diet.");
      setTimeout(() => setFlashSuccess(null), 6000);
    } catch (e) {
      setFlashError(safeMsg(e));
    }
  }

  async function handleOverride() {
    if (!appId) return;
    if (ovReason.trim().length < 5) {
      setFlashError("A reason (min 5 characters) is required for an emergency override.");
      return;
    }
    const passMark = Number(ovPassMark || 50);

    // Every unit must have a valid score — every registered course, or the
    // single interview.
    const scores: { exam_course_id: number | null; score: number }[] = [];
    for (const c of courses) {
      const raw = ovScores[String(c.course_id)];
      if (raw === undefined || raw === "") {
        setFlashError(
          isInterview
            ? "Enter the interview score before overriding."
            : "You must enter a score for every registered course before overriding."
        );
        return;
      }
      const n = Number(raw);
      if (isNaN(n) || n < 0 || n > 100) {
        setFlashError(`Score for ${c.course_code || c.course_name} must be between 0 and 100.`);
        return;
      }
      scores.push({ exam_course_id: c.is_interview ? null : Number(c.course_id), score: n });
    }
    if (!scores.length) {
      setFlashError("This candidate has nothing to score.");
      return;
    }

    const allPass = scores.every((s) => s.score >= passMark);
    const derived = isInterview
      ? allPass
        ? "RECOMMENDED"
        : "NOT RECOMMENDED"
      : allPass
        ? "PASSED"
        : scores.some((s) => s.score >= passMark)
          ? "PARTIAL PASS"
          : "FAILED";
    const warn =
      `This override will set the result to ${derived} (derived from the scores you entered) and ` +
      (ovPublish ? "PUBLISH it, closing grading on this candidate for good." : "save it as a draft.") +
      " Continue?";
    if (!window.confirm(warn)) return;

    setFlashError(null);
    try {
      const res = await override({
        appId,
        body: {
          reason: ovReason.trim(),
          scores,
          pass_mark: passMark,
          publish: ovPublish,
        },
      }).unwrap();
      setFlashSuccess(res?.message || "Emergency override applied.");
      setOvReason("");
      setOvScores({});
      setShowOverride(false);
      setTimeout(() => setFlashSuccess(null), 6000);
    } catch (e) {
      setFlashError(safeMsg(e));
    }
  }

  // Live preview of the emergency override, derived from the entered scores.
  const ovPassMarkNum = Number(ovPassMark || 50);
  const ovBreakdown = courses.map((c: any) => {
    const raw = ovScores[String(c.course_id)];
    const n = raw === undefined || raw === "" ? null : Number(raw);
    return {
      ...c,
      scoreNum: n,
      passed: n !== null && !isNaN(n) ? n >= ovPassMarkNum : null,
    };
  });
  const ovAllScored =
    courses.length > 0 &&
    ovBreakdown.every((b: any) => b.scoreNum !== null && !isNaN(b.scoreNum));
  const ovPassedCount = ovBreakdown.filter((b: any) => b.passed === true).length;
  const ovDerived = !ovAllScored
    ? null
    : isInterview
      ? ovPassedCount === courses.length
        ? "recommended"
        : "not_recommended"
      : ovPassedCount === courses.length
        ? "passed"
        : ovPassedCount === 0
          ? "failed"
          : "partial_pass";
  const ovIsPositive = ovDerived === "passed" || ovDerived === "recommended";

  // Published is final. The API refuses every write on a published result, so
  // the controls are withdrawn rather than left to fail on submit. published_at
  // is the secretariat's later release, which implies publication.
  const isPublished = !!((resultMeta as any)?.finalized_at || (resultMeta as any)?.published_at);
  const isReleased = !!(resultMeta as any)?.published_at;

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-4 md:p-6 pb-20">
      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl flex-shrink-0">
              <Star className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">
                {isInterview ? "Interview Grading" : "Course Grading"}
              </h1>
              <p className="text-gray-400 text-sm mt-2 font-medium">
                Application{" "}
                <span className="font-mono text-xs">{appId}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => refetch()}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-indigo-500 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={() => navigate(-1)}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Flash messages */}
      {flashError && (
        <div className="bg-rose-50 border border-rose-100 rounded-2xl px-6 py-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          <p className="text-sm font-bold text-rose-600">{flashError}</p>
        </div>
      )}
      {flashSuccess && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-6 py-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <p className="text-sm font-bold text-emerald-600">{flashSuccess}</p>
        </div>
      )}

      {/* Body */}
      {isLoading ? (
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-20 flex flex-col items-center justify-center">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400">
            Loading grades…
          </span>
        </div>
      ) : isError ? (
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-20 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-rose-200 mb-4" />
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
            Failed to load grades
          </p>
          <button
            onClick={() => refetch()}
            className="mt-6 px-6 py-2.5 rounded-xl bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest"
          >
            Retry
          </button>
        </div>
      ) : courses.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-20 text-center">
          <BookOpen className="w-12 h-12 mx-auto text-gray-100 mb-4" />
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
            No registered courses for this application
          </p>
          <Link
            to={`/exams/admin/applications/${appId}/courses`}
            className="inline-block mt-6 px-6 py-2.5 rounded-xl bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest"
          >
            Register Courses First
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Overridden-by-Chairman notice (admin-only) */}
          {resultMeta?.is_override && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-[2rem] p-6 flex items-start gap-4">
              <div className="w-11 h-11 bg-amber-100 rounded-2xl flex items-center justify-center border border-amber-200 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-600">
                  Result Overridden by Chairman
                </div>
                <p className="text-sm text-gray-700 mt-1">
                  This result was set via emergency override
                  {resultMeta.override_by_name ? ` by ${resultMeta.override_by_name}` : ""}
                  {resultMeta.override_at
                    ? ` on ${new Date(resultMeta.override_at).toLocaleString()}`
                    : ""}
                  , bypassing the 3-examiner average.
                </p>
                {resultMeta.override_reason && (
                  <p className="text-xs text-gray-500 italic mt-1.5">
                    Reason: {resultMeta.override_reason}
                  </p>
                )}
                {Array.isArray(resultMeta.override_courses) && resultMeta.override_courses.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {resultMeta.override_courses.map((c: any, i: number) => (
                      <span
                        key={i}
                        className={`text-[10px] font-black px-2.5 py-1 rounded-lg border ${
                          c.passed
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-rose-50 text-rose-700 border-rose-200"
                        }`}
                      >
                        {c.course_code || c.course_name}: {c.score}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Published — the result is closed to every kind of edit */}
          {isPublished && (
            <div className="bg-gray-900 rounded-[2rem] p-6 flex items-start gap-4">
              <div className="w-11 h-11 bg-white/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-white/60">
                  Result Published — Final
                </div>
                <p className="text-sm text-white mt-1">
                  Published by the Chief Examiner
                  {(resultMeta as any)?.finalized_at
                    ? ` on ${new Date((resultMeta as any).finalized_at).toLocaleString()}`
                    : ""}
                  . Grading, compilation and emergency override are closed for this candidate.
                </p>
                <p className="text-xs text-white/50 mt-1.5">
                  {isReleased
                    ? `Released to the candidate on ${new Date(
                        (resultMeta as any).published_at,
                      ).toLocaleString()}.`
                    : "Not yet visible to the candidate — the secretariat releases the diet."}
                </p>
              </div>
            </div>
          )}

          {/* EMERGENCY OVERRIDE — bypasses the ≥3-examiner average protocol.
              Gated on exam.result.publish rather than on marking: setting a
              result single-handedly is the publishing office's power, not a
              marker's, and the API refuses it to anyone else. */}
          <div
            className={`bg-white border-2 border-amber-200 rounded-[2rem] p-6 md:p-8 shadow-sm ${
              isPublished || !canOverride ? "hidden" : ""
            }`}
          >
            <button
              type="button"
              onClick={() => setShowOverride((v) => !v)}
              className="w-full flex items-center gap-3 text-left"
            >
              <div className="w-11 h-11 bg-amber-50 rounded-2xl flex items-center justify-center border border-amber-200 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-600">
                  Emergency Override — Chairman Scoring
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isInterview ? (
                    <>
                      Enter the candidate's interview score. The result is derived automatically — recommended at{" "}
                      {"≥"} the pass mark, otherwise not recommended. Bypasses the 3-examiner average — audited.
                    </>
                  ) : (
                    <>
                      Enter a score for <span className="font-black">every</span> registered course. The result is
                      derived automatically (a pass requires passing all courses). Bypasses the 3-examiner average —
                      audited.
                    </>
                  )}
                </p>
              </div>
              <span className="text-[10px] font-black uppercase text-amber-600">
                {showOverride ? "Hide" : "Open"}
              </span>
            </button>

            {showOverride && (
              <div className="mt-6 space-y-5">
                {/* Pass mark */}
                <div className="flex items-center gap-3">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Pass mark
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={ovPassMark}
                    onChange={(e) => setOvPassMark(e.target.value)}
                    className="w-24 border border-gray-200 rounded-xl px-3 py-2 text-sm font-black text-gray-900 outline-none focus:border-amber-500"
                  />
                  <span className="text-[11px] text-gray-400">out of 100</span>
                </div>

                {/* Per-course score inputs */}
                <div className="border border-gray-100 rounded-2xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 text-[10px] font-black uppercase tracking-widest text-gray-400 flex items-center justify-between">
                    <span>{isInterview ? unitLabel : `${unitLabel} (${courses.length})`}</span>
                    <span>Score / {ovPassMarkNum}</span>
                  </div>
                  {courses.length === 0 ? (
                    <div className="px-4 py-6 text-center text-[11px] font-black uppercase tracking-widest text-gray-300">
                      Nothing to score
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {ovBreakdown.map((c: any) => (
                        <div key={c.course_id} className="flex items-center gap-3 px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-gray-800 truncate">{c.course_name}</div>
                            {c.course_code && (
                              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{c.course_code}</div>
                            )}
                          </div>
                          {c.passed !== null && (
                            <span
                              className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                                c.passed
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-rose-50 text-rose-700 border-rose-200"
                              }`}
                            >
                              {isInterview
                                ? c.passed
                                  ? "Recommended"
                                  : "Not Recommended"
                                : c.passed
                                  ? "Pass"
                                  : "Fail"}
                            </span>
                          )}
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={ovScores[String(c.course_id)] ?? ""}
                            onChange={(e) =>
                              setOvScores((prev) => ({ ...prev, [String(c.course_id)]: e.target.value }))
                            }
                            placeholder="—"
                            className="w-20 border border-gray-200 rounded-lg px-3 py-2 text-sm text-right font-black text-gray-900 outline-none focus:border-amber-500"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Live derived result */}
                <div
                  className={`rounded-2xl px-4 py-3 border-2 flex items-center justify-between ${
                    !ovAllScored
                      ? "bg-gray-50 border-gray-200"
                      : ovIsPositive
                        ? "bg-emerald-50 border-emerald-200"
                        : ovDerived === "failed" || ovDerived === "not_recommended"
                          ? "bg-rose-50 border-rose-200"
                          : "bg-amber-50 border-amber-200"
                  }`}
                >
                  <div className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                    Derived Result
                  </div>
                  <div className="text-sm font-black uppercase tracking-widest text-gray-900">
                    {!ovAllScored
                      ? isInterview
                        ? "Enter the interview score"
                        : `Score all ${courses.length} course${courses.length !== 1 ? "s" : ""}`
                      : isInterview
                        ? (ovDerived ?? "").replace(/_/g, " ")
                        : `${(ovDerived ?? "").replace(/_/g, " ")} · ${ovPassedCount}/${courses.length} passed`}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Reason for override <span className="text-amber-600">(required)</span>
                  </label>
                  <textarea
                    rows={2}
                    value={ovReason}
                    onChange={(e) => setOvReason(e.target.value)}
                    placeholder="e.g. Examiner unavailable before deadline — chairman decision per board minute #…"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-amber-500 placeholder-gray-400"
                  />
                </div>

                <label className="flex items-center gap-2 text-xs font-bold text-gray-600">
                  <input
                    type="checkbox"
                    checked={ovPublish}
                    onChange={(e) => setOvPublish(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Publish immediately — closes grading on this candidate (uncheck to save a draft)
                </label>

                <button
                  onClick={handleOverride}
                  disabled={overriding || !ovAllScored}
                  className="w-full py-4 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-amber-700 transition-all active:scale-95 disabled:opacity-40"
                >
                  {overriding ? "Applying…" : ovPublish ? "Override & Publish Final Result" : "Override (Save Draft)"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
